import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { colorDe, iniciales } from '../lib/avatar.js';
import { hashDeRuta, type VistaSeguimientoRuta } from '../lib/hash-route.js';
import {
  DIAS_PLAZO_SECUENCIA, GRUPOS_INFO, GRUPOS_PENDIENTES, aplicarPropuesta, darDeBaja, diaDeMadrid, fraseDiaRelativo, fraseGrupoDia, fraseHora,
  leerDetalleCorreo, leerPanelSecuencia, leerVista, meContesto,
  type Aviso, type CanalToque, type Ciclo, type EstadoAccion, type FilaSecuencia, type GrupoAviso, type ItemEnviado, type PanelSecuencia,
  type DetalleCorreo, type PedirSeguimiento, type Ref, type Resultado, type ToquesHechos, type ViaEnviado, type VistaSeguimiento,
} from '../lib/seguimiento-client.js';

/**
 * LA PESTAÑA «SEGUIMIENTO» DE FLUXO DASHBOARD (solo donde el servidor la enciende: hoy, fluxo).
 * 20-oct: la redacción del panel, aparcada (FLUXO_SEGUIMIENTO_REDACCION_APARCADA): el panel ya no
 * escribe ni manda correos ni WhatsApp, eso lo hace la skill de seguimiento. Joan: «¿podemos hacer
 * el panel full visual y quitar lo que ya no sirve?». Fuera Borradores y WhatsApp (con sus botones de
 * enviar, editar, dossier, «Lo reviso yo» y «Me contestó» de los WhatsApp). Queda:
 *
 *  - Avisos, arriba y como siempre: solo si hay algo. A la vista, lo que falló (lo que no se pudo
 *    terminar, las llamadas de hoy que no llegaron al dialer) y, solo donde las acciones no se
 *    aplican solas, lo que espera su botón (una propuesta, una baja). Lo que es solo para informar
 *    (derivaciones creadas o sin datos, los cambios aplicados solos) va plegado en una línea.
 *  - Secuencia, la primera (#/seguimiento): las cifras, las personas por ciclo y las personas en
 *    tres grupos: «Te toca llamar» (la que más espera, arriba; las de con retraso, en ámbar), «En
 *    curso» (la llamada del ciclo, hecha) y «Salieron», plegado. Cada fila se lee de un vistazo: una
 *    pista de 8 tramos con los ciclos y una barra de días contra los 21 que dura la secuencia, más los
 *    toques por canal y dónde está en una línea. Solo lectura y sin botones de llamada (las llamadas
 *    van a la Manual Call de «Cola del día» en GHL); se lee aparte (GET /api/seguimiento/secuencia):
 *    una vez al entrar y, con la vista abierta, cada minuto.
 *  - Enviados: 7 días, por día, con el estado en color (enviado, sin confirmar, rebotó), también lo
 *    que salió de hola@ fuera del panel (la skill, Gmail), con su filtro.
 *
 * La pantalla no decide nada: lee GET /api/seguimiento/vista y, tras cada botón, vuelve a leer: lo
 * que diga el servidor manda. Las vistas son puras (sin hooks); lo que se elige o se abre vive en
 * SeguimientoPestana y no se pierde al cambiar de vista.
 */

/** Cada cuánto se vuelve a leer mientras la pestaña está abierta (la cola avanza, llegan avisos). */
export const REFRESCO_MS = 60_000;
/** Cuántas líneas se ven de cada grupo de avisos antes de «y N más». */
export const AVISOS_A_LA_VISTA = 5;

export interface FalloItem {
  texto: string;
  /** Cómo estaba la cosa al fallar: si el servidor ya dice otra cosa, el fallo no se enseña. */
  firma: string;
}

/** El mapa de lo que no salió de cada botón (null: salió), o 'apagado'. */
export type SalidaBoton = Record<string, string | null> | 'apagado';

export interface UsoSeguimiento {
  /** null mientras no se sabe (o si no se ha podido leer): sin pestaña. */
  datos: VistaSeguimiento | null;
  /** La última lectura falló (se vuelve a intentar sola). */
  sinLeer: boolean;
  fallos: Record<string, FalloItem>;
  /** Claves con un botón pulsado que aún no ha contestado. */
  enCurso: string[];
  /**
   * Pulsa un botón de una o varias cosas (`claves`): lo hace, apunta lo que no salió y vuelve a
   * leer antes de soltar los botones. true si salió todo; null si no se hizo (una de esas cosas
   * ya tenía un botón en marcha, o no hay sesión). `fn` se llama en el mismo clic, antes de
   * esperar nada: lo que necesite el clic (abrir una ventana) va al principio.
   */
  ejecutar: (claves: string[], fn: (p: PedirSeguimiento) => Promise<SalidaBoton>, sinRespuesta: string, firmas: Record<string, string>) => Promise<boolean | null>;
  /** De quién es la sesión (lo leído de otra no se enseña). Sin ella, la vista «Secuencia» no se lee. */
  identidad?: string | null;
  /** Lee la vista «Secuencia» (lanza si falla). */
  leerSecuencia?: () => Promise<PanelSecuencia | 'apagado'>;
}

/**
 * Lee la pestaña. En el panel basta una lectura para saber si este inquilino la tiene (con
 * `{ encendido: false }` no vuelve a preguntar); con la pestaña abierta (`activa`) la mantiene al día.
 */
export function useSeguimiento(pedir: PedirSeguimiento | null, identidad: string | null, activa = false): UsoSeguimiento {
  const [vista, setVista] = useState<{ identidad: string; datos: VistaSeguimiento } | null>(null);
  const [sinLeer, setSinLeer] = useState<string | null>(null);
  const [fallos, setFallos] = useState<{ identidad: string | null; porClave: Record<string, FalloItem> }>({ identidad: null, porClave: {} });
  const [enCurso, setEnCurso] = useState<{ identidad: string | null; claves: string[] }>({ identidad: null, claves: [] });
  const pedirRef = useRef(pedir);
  pedirRef.current = pedir;
  const vistaRef = useRef(vista);
  vistaRef.current = vista;
  const enCursoRef = useRef(new Set<string>());
  // Las lecturas pueden volver en cualquier orden: solo se aplica la más nueva.
  const contador = useRef({ pedida: 0, aplicada: 0 });

  /** Una lectura. Devuelve lo leído (null si falló). */
  const leer = async (): Promise<VistaSeguimiento | null> => {
    const p = pedirRef.current;
    if (!p || !identidad) return null;
    const n = ++contador.current.pedida;
    try {
      const datos = await leerVista(p);
      if (n > contador.current.aplicada) {
        contador.current.aplicada = n;
        setVista({ identidad, datos });
        setSinLeer(null);
      }
      return datos;
    } catch {
      if (n > contador.current.aplicada) setSinLeer(identidad);
      return null;
    }
  };
  const leerRef = useRef(leer);
  leerRef.current = leer;

  useEffect(() => {
    if (!identidad) return;
    // Fuera de la pestaña, con la respuesta ya leída, no hay nada que mantener al día.
    if (!activa && vistaRef.current?.identidad === identidad) return;
    let vivo = true;
    let temporizador: ReturnType<typeof setTimeout> | null = null;
    const vuelta = async (): Promise<void> => {
      const datos = await leerRef.current();
      if (!vivo || datos?.encendido === false) return;
      // En la pestaña, siempre; en el panel, solo mientras no se sepa si la tiene.
      if (activa || !datos) temporizador = setTimeout(() => { void vuelta(); }, REFRESCO_MS);
    };
    void vuelta();
    return () => { vivo = false; if (temporizador) clearTimeout(temporizador); };
  }, [identidad, activa]);

  const deEsta = <T,>(x: { identidad: string | null }, vacio: T, v: T): T => (x.identidad === identidad ? v : vacio);

  const ejecutar: UsoSeguimiento['ejecutar'] = async (claves, fn, sinRespuesta, firmas) => {
    const p = pedirRef.current;
    if (!p || !identidad || claves.length === 0 || claves.some((c) => enCursoRef.current.has(c))) return null;
    for (const c of claves) enCursoRef.current.add(c);
    setEnCurso((e) => ({ identidad, claves: [...deEsta(e, [], e.claves), ...claves] }));
    const apuntar = (textos: Record<string, string | null>): void => setFallos((f) => {
      const porClave = { ...deEsta(f, {}, f.porClave) };
      for (const [c, t] of Object.entries(textos)) {
        if (t) porClave[c] = { texto: t, firma: firmas[c] ?? '' }; else delete porClave[c];
      }
      return { identidad, porClave };
    });
    apuntar(Object.fromEntries(claves.map((c) => [c, null])));
    let todo = false;
    try {
      const r = await fn(p);
      if (r === 'apagado') {
        setVista({ identidad, datos: { encendido: false } });
      } else {
        apuntar(r);
        todo = claves.every((c) => !r[c]);
      }
    } catch {
      apuntar(Object.fromEntries(claves.map((c) => [c, sinRespuesta])));
    } finally {
      // Lo que diga el servidor manda: se vuelve a leer antes de soltar el botón.
      await leerRef.current();
      for (const c of claves) enCursoRef.current.delete(c);
      setEnCurso((e) => ({ identidad, claves: deEsta(e, [], e.claves).filter((x) => !claves.includes(x)) }));
    }
    return todo;
  };

  return {
    datos: vista && vista.identidad === identidad ? vista.datos : null,
    sinLeer: sinLeer !== null && sinLeer === identidad,
    fallos: deEsta(fallos, {}, fallos.porClave),
    enCurso: deEsta(enCurso, [], enCurso.claves),
    ejecutar,
    identidad,
    leerSecuencia: async () => {
      const p = pedirRef.current;
      if (!p) throw new Error('sin_sesion');
      return leerPanelSecuencia(p);
    },
  };
}

/** Lo leído de la vista «Secuencia»: null mientras no se sabe; `sinLeer`, la última lectura falló. */
export interface LecturaSecuencia { datos: PanelSecuencia | null; sinLeer: boolean }

/**
 * La vista «Secuencia» se lee aparte: una vez al entrar (para que su tarjeta diga cuántas personas hay
 * dentro y no un guion, 16-oct) y, mientras está abierta, cada REFRESCO_MS. Cerrada no se repite: la
 * vista de siempre no la necesita y así no carga la base en cada vuelta. Lo leído se guarda por sesión.
 */
export function usePanelSecuencia(uso: UsoSeguimiento, abierta: boolean): LecturaSecuencia {
  const [lectura, setLectura] = useState<{ identidad: string; datos: PanelSecuencia | null; sinLeer: boolean } | null>(null);
  const usoRef = useRef(uso);
  usoRef.current = uso;
  const identidad = uso.identidad ?? null;
  // Solo donde el seguimiento esta encendido (con la vista de siempre ya leida): apagado no se pregunta nada mas.
  const encendido = uso.datos?.encendido === true;
  useEffect(() => {
    if (!identidad || !encendido || !usoRef.current.leerSecuencia) return;
    let vivo = true;
    let temporizador: ReturnType<typeof setTimeout> | null = null;
    const vuelta = async (): Promise<void> => {
      try {
        const r = await usoRef.current.leerSecuencia!();
        if (!vivo) return;
        setLectura({ identidad, datos: r === 'apagado' ? null : r, sinLeer: false });
      } catch {
        if (!vivo) return;
        setLectura((x) => ({ identidad, datos: x?.identidad === identidad ? x.datos : null, sinLeer: true }));
      }
      if (vivo && abierta) temporizador = setTimeout(() => { void vuelta(); }, REFRESCO_MS);
    };
    void vuelta();
    return () => { vivo = false; if (temporizador) clearTimeout(temporizador); };
  }, [abierta, identidad, encendido]);
  return lectura && lectura.identidad === identidad ? { datos: lectura.datos, sinLeer: lectura.sinLeer } : { datos: null, sinLeer: false };
}

/* ------------------------------------------------------------------------- *
 * Textos
 * ------------------------------------------------------------------------- */

/** Lo que se dice cuando un botón falla y el servidor no da su razón. */
const RECHAZOS: Record<string, string> = {
  no_encontrado: 'Ya no está.',
  en_marcha: 'Ya se está haciendo.',
  bloqueado: 'No se puede: DND, baja o email cambiado en GHL.',
  fuera: 'Ya no está en la secuencia.',
  sin_clave_ghl: 'No hay clave de GHL para este CRM.',
};
const SIN_RESPUESTA = {
  aplicar: 'No se sabe si se ha aplicado: míralo en GHL antes de repetirlo.',
  baja: 'No se sabe si se ha dado de baja: míralo en GHL antes de repetirlo.',
  guardar: 'No se ha podido guardar.',
} as const;

/** Lo que no salió de un botón (null: salió). */
export function textoDeResultado(r: Exclude<Resultado, 'apagado'>, sinRespuesta: string): string | null {
  if (r === 'no_existe') return RECHAZOS.no_encontrado;
  if ('rechazo' in r) return r.detalle ?? RECHAZOS[r.rechazo] ?? sinRespuesta;
  return null;
}

export const claveDe = (x: Ref | { origen: ItemEnviado['origen']; id: string }): string => `${x.origen}:${x.id}`;
/** El estado de una cosa tal como se ve: si cambia, el fallo de antes ya no se enseña. */
export const firmaDe = (x: unknown): string => JSON.stringify(x);
const juntar = (partes: Array<string | null | false | undefined>): string => partes.filter((x): x is string => !!x).join(' · ');

/* ------------------------------------------------------------------------- *
 * Lo visual: avatar, chip, canal y ciclo
 * ------------------------------------------------------------------------- */

// Las iniciales y el color del avatar viven en lib/avatar.ts (los usa tambien el panel).
export { iniciales, colorDe };

function Avatar({ nombre }: { nombre: string }) {
  return <span className={`seg-avatar seg-avatar--${colorDe(nombre)}`} aria-hidden="true">{iniciales(nombre)}</span>;
}

function IconoCanal({ canal }: { canal: CanalToque }) {
  if (canal === 'llamada') {
    return (
      <svg className="seg-canal seg-canal--llamada" viewBox="0 0 16 16" width="14" height="14" role="img" aria-label="Llamada">
        <path d="M5.3 2.2 3.6 2.6c-.8.2-1.3 1-1.1 1.8 1.2 4.4 4.6 7.9 9 9.1.8.2 1.6-.3 1.8-1.1l.4-1.7-2.9-1.4-1.3 1.3a7.6 7.6 0 0 1-3.5-3.5l1.3-1.3-1.4-2.9Z"
          fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      </svg>
    );
  }
  return canal === 'email' ? (
    <svg className="seg-canal" viewBox="0 0 16 16" width="14" height="14" role="img" aria-label="Correo">
      <rect x="1.5" y="3" width="13" height="10" rx="2" fill="none" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.5 4.5 8 8.6l5.5-4.1" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg className="seg-canal seg-canal--wa" viewBox="0 0 16 16" width="14" height="14" role="img" aria-label="WhatsApp">
      <path d="M8 1.6a6.3 6.3 0 0 0-5.4 9.6L1.8 14.4l3.3-.8A6.3 6.3 0 1 0 8 1.6Z" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

/** El ciclo de un envío como puntos (rellenos los que ya van) y su rótulo «3/8», que se entiende sin pasar el ratón. */
export function PuntosCiclo({ ciclo }: { ciclo: Ciclo | null }) {
  if (!ciclo) return null;
  return (
    <span className="seg-ciclo" title={`Ciclo ${ciclo.n} de ${ciclo.de} de la secuencia`}>
      <span className="seg-puntos" aria-hidden="true">
        {Array.from({ length: ciclo.de }, (_, i) => <span key={i} className={`seg-punto${i < ciclo.n ? ' seg-punto--hecho' : ''}`} />)}
      </span>
      <span className="seg-ciclo-n" aria-label={`Ciclo ${ciclo.n} de ${ciclo.de}`}>{ciclo.n}/{ciclo.de}</span>
    </span>
  );
}

/**
 * Nombre en negrita, empresa en gris, el chip del motivo (si lo hay) y, en Enviados (donde se mezclan
 * correos y WhatsApp), el icono del canal.
 */
function Cabeza({ x, canal, chip, extra }: { x: { nombre: string; empresa: string | null }; canal?: 'email' | 'whatsapp'; chip: string | null; extra?: ReactNode }) {
  return (
    <div className="seg-cabeza">
      <span className="seg-nombre">{x.nombre}</span>
      {x.empresa && <span className="seg-empresa">{x.empresa}</span>}
      {canal && <IconoCanal canal={canal} />}
      {chip && <span className="seg-chip">{chip}</span>}
      {extra}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Pestañas y vistas
 * ------------------------------------------------------------------------- */

/** Las pestañas de Fluxo Dashboard. Solo se pintan donde el seguimiento está encendido. */
export function PestanasPanel({ activa }: { activa: 'panel' | 'seguimiento' }) {
  return (
    <nav className="tabs pestanas-panel" aria-label="Fluxo Dashboard">
      <a href="#/" className={`tab${activa === 'panel' ? ' active' : ''}`} aria-current={activa === 'panel' ? 'page' : undefined}>Panel</a>
      <a href="#/seguimiento" className={`tab${activa === 'seguimiento' ? ' active' : ''}`} aria-current={activa === 'seguimiento' ? 'page' : undefined}>Seguimiento</a>
    </nav>
  );
}

const VISTAS: Record<VistaSeguimientoRuta, { nombre: string; pie: string }> = {
  secuencia: { nombre: 'Secuencia', pie: 'personas dentro' },
  enviados: { nombre: 'Enviados', pie: 'últimos 7 días' },
};

/**
 * Las vistas como tarjetas, con su número grande (en Secuencia, las personas dentro, que se sabe al
 * leerla: hasta entonces, «-»; en Enviados, lo de 7 días). `pies`: el pie que dice qué cuenta cada una
 * cuando hay más que decir (piesTarjetas); si no, el de siempre.
 */
/**
 * La página de los WhatsApp del seguimiento (20-oct): la deja la skill en claude.ai al acabar cada lote, con el
 * texto de cada uno, el botón a WhatsApp Web que marca lo enviado y quién ha respondido. Solo existe para Fluxo,
 * como esta pestaña.
 */
export const PAGINA_WHATSAPP = 'https://claude.ai/artifact/3yfCKJWxcTJJFDBQCSZE2i';

export function VistasSeguimiento({ activa, cuentas, pies }: {
  activa: VistaSeguimientoRuta; cuentas: Partial<Record<VistaSeguimientoRuta, number | null>> | null; pies?: Partial<Record<VistaSeguimientoRuta, ReactNode>>;
}) {
  return (
    <nav className="seg-tarjetas" aria-label="Seguimiento">
      {(Object.keys(VISTAS) as VistaSeguimientoRuta[]).map((v) => {
        const n = cuentas?.[v] ?? null;
        return (
          <a key={v} href={hashDeRuta({ kind: 'seguimiento', vista: v })} className={`seg-tarjeta seg-tarjeta--${v}${v === activa ? ' activa' : ''}`}
            aria-current={v === activa ? 'page' : undefined}>
            <span className="seg-tarjeta-nombre">{VISTAS[v].nombre}</span>
            <span className={`seg-tarjeta-num${n === 0 ? ' seg-tarjeta-num--cero' : ''}`}>{n ?? '-'}</span>
            <span className="seg-tarjeta-pie">{pies?.[v] ?? VISTAS[v].pie}</span>
          </a>
        );
      })}
    </nav>
  );
}

/** El enlace a la página de los WhatsApp del seguimiento (PAGINA_WHATSAPP), debajo de las tarjetas: abre en otra pestaña. */
export function EnlaceListaWhatsapp() {
  return (
    <a href={PAGINA_WHATSAPP} target="_blank" rel="noopener noreferrer" className="seg-lista-whatsapp">
      <span className="seg-lista-whatsapp-nombre">Lista de WhatsApp del seguimiento</span>
      <span className="seg-lista-whatsapp-pie">los de cada lote, para mandarlos desde WhatsApp Web; marca lo enviado y quién ha respondido</span>
      <span className="seg-lista-whatsapp-flecha" aria-hidden="true">↗</span>
    </a>
  );
}

/** Lo que dice el pie de Enviados: cuántos correos y cuántos WhatsApp, con su icono, y de cuántos días. */
export function piesTarjetas(datos: Encendido): Partial<Record<VistaSeguimientoRuta, ReactNode>> {
  const correos = datos.enviados.filter((x) => x.canal === 'email').length;
  const whatsapps = datos.enviados.length - correos;
  return {
    enviados: (
      <span className="seg-tarjeta-canales">
        <span><IconoCanal canal="email" />{correos} {correos === 1 ? 'correo' : 'correos'}</span>
        <span><IconoCanal canal="whatsapp" />{whatsapps} WhatsApp</span>
        <span>7 días</span>
      </span>
    ),
  };
}

/* ------------------------------------------------------------------------- *
 * La pestaña: el estado de lo que se elige y se abre
 * ------------------------------------------------------------------------- */

export interface EstadoPestana {
  /** «Me contestó» pide un segundo clic. */
  contesto: string | null;
  /** Enviados por canal (16-oct: Todos, Correos, WhatsApp) y, encima, solo lo de fuera del panel. */
  filtro: 'todos' | 'email' | 'whatsapp';
  soloFuera: boolean;
  /** El enviado desplegado (su texto entero). */
  desplegado: string | null;
  /** El contenido del proveedor, leído al abrir un correo enviado. */
  correos: Record<string, DetalleCorreo>;
  /** Los grupos de avisos con todas sus líneas a la vista («y N más»). */
  grupos: GrupoAviso[];
}

export const ESTADO_INICIAL: EstadoPestana = {
  contesto: null, filtro: 'todos', soloFuera: false, desplegado: null, correos: {}, grupos: [],
};

export function SeguimientoPestana({ uso, vista = 'secuencia', ahora }: { uso: UsoSeguimiento; vista?: VistaSeguimientoRuta; ahora?: Date }) {
  const [e, setE] = useState<EstadoPestana>(ESTADO_INICIAL);
  const secuencia = usePanelSecuencia(uso, vista === 'secuencia');
  const cambiar = (c: Partial<EstadoPestana> | ((x: EstadoPestana) => Partial<EstadoPestana>)): void =>
    setE((x) => ({ ...x, ...(typeof c === 'function' ? c(x) : c) }));
  const datos = uso.datos;
  if (!datos || !datos.encendido) {
    return (
      <section className="seg-pantalla">
        <p className="seg-vacio">{uso.sinLeer ? 'No se ha podido leer el seguimiento. Se vuelve a intentar solo.' : 'Cargando…'}</p>
      </section>
    );
  }
  const momento = ahora ?? new Date();
  /** Un botón de una sola cosa con una ruta que devuelve un Resultado. */
  const uno = (x: object, clave: string, accion: (p: PedirSeguimiento) => Promise<Resultado>, sinRespuesta: string) =>
    uso.ejecutar([clave], async (p) => {
      const r = await accion(p);
      return r === 'apagado' ? 'apagado' : { [clave]: textoDeResultado(r, sinRespuesta) };
    }, sinRespuesta, { [clave]: firmaDe(x) });

  const m: Manejadores = {
    pedirContesto: (clave) => cambiar({ contesto: clave }),
    contestar: (x, canal) => {
      cambiar({ contesto: null });
      if (!x.secuenciaId) return;
      const sid = x.secuenciaId;
      void uno(x, `contesto:${claveDe(x)}`, (p) => meContesto(p, sid, canal), SIN_RESPUESTA.guardar);
    },
    accionAviso: (a) => {
      const acc = a.accion;
      if (!acc) return;
      const ref = { origen: acc.origen, id: acc.id };
      if (acc.tipo === 'baja') void uno(a, a.clave, (p) => darDeBaja(p, ref), SIN_RESPUESTA.baja);
      else void uno(a, a.clave, (p) => aplicarPropuesta(p, ref), SIN_RESPUESTA.aplicar);
    },
    verGrupo: (g) => cambiar((x) => ({ grupos: x.grupos.includes(g) ? x.grupos.filter((y) => y !== g) : [...x.grupos, g] })),
    filtrar: (f) => cambiar({ filtro: f }),
    verFuera: (si) => cambiar({ soloFuera: si }),
    desplegar: (clave, recargar = false) => {
      const abriendo = recargar || e.desplegado !== clave;
      cambiar({ desplegado: abriendo ? clave : null });
      const item = datos.enviados.find((x) => claveDe(x) === clave);
      if (!abriendo || !item || item.canal !== 'email' || (!recargar && e.correos[clave])) return;
      const peticion = `correo:${clave}`;
      void uso.ejecutar([peticion], async (p) => {
        const correo = await leerDetalleCorreo(p, item);
        cambiar((x) => ({ correos: { ...x.correos, [clave]: correo } }));
        return { [peticion]: null };
      }, 'No se ha podido leer el correo completo.', { [peticion]: firmaDe(item) });
    },
  };

  const props: PropsVista = { datos, estado: e, fallos: uso.fallos, enCurso: uso.enCurso, ahora: momento, m, secuencia };
  return (
    <section className="seg-pantalla">
      {uso.sinLeer && <p className="seg-nota">No se ha podido volver a leer: lo que ves puede no estar al día.</p>}
      <BloqueAvisos {...props} />
      <VistasSeguimiento activa={vista} cuentas={{ enviados: datos.cuentas.enviados, secuencia: secuencia.datos?.cifras.enSecuencia ?? null }} pies={piesTarjetas(datos)} />
      <EnlaceListaWhatsapp />
      <VistaElegida vista={vista} {...props} />
    </section>
  );
}

type Encendido = Extract<VistaSeguimiento, { encendido: true }>;

export interface Manejadores {
  pedirContesto: (clave: string | null) => void;
  contestar: (x: { origen: ItemEnviado['origen']; id: string; secuenciaId: string | null }, canal: 'email' | 'whatsapp') => void;
  /** El botón de un aviso «Por confirmar»: aplicar la propuesta o dar de baja. */
  accionAviso: (a: Aviso) => void;
  verGrupo: (g: GrupoAviso) => void;
  filtrar: (f: EstadoPestana['filtro']) => void;
  /** Enviados: solo lo que salió de hola@ fuera del panel (o todo). */
  verFuera: (si: boolean) => void;
  desplegar: (clave: string, recargar?: boolean) => void;
}

export interface PropsVista {
  datos: Encendido;
  estado: EstadoPestana;
  fallos: Record<string, FalloItem>;
  enCurso: string[];
  ahora: Date;
  m: Manejadores;
  /** La vista «Secuencia» (se lee aparte); sin ella, «Cargando…». */
  secuencia?: LecturaSecuencia;
}

export function VistaElegida(p: PropsVista & { vista: VistaSeguimientoRuta }) {
  if (p.vista === 'enviados') return <VistaEnviados {...p} />;
  return <VistaSecuencia lectura={p.secuencia ?? { datos: null, sinLeer: false }} ahora={p.ahora} />;
}


/** El fallo de una cosa, si sigue como estaba al fallar. */
const falloDe = (fallos: Record<string, FalloItem>, clave: string, x: unknown): string | null => {
  const f = fallos[clave];
  return f && f.firma === firmaDe(x) ? f.texto : null;
};

function Vacia({ texto, detalle }: { texto: string; detalle?: string | null }) {
  return (
    <div className="seg-vacio">
      <p className="seg-vacio-texto">{texto}</p>
      {detalle && <p className="seg-vacio-detalle">{detalle}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * Avisos
 * ------------------------------------------------------------------------- */

const GRUPO: Record<GrupoAviso, { titulo: string; tono: 'accion' | 'mal' | 'aviso' | 'bien' | 'neutro' }> = {
  porConfirmar: { titulo: 'Por confirmar', tono: 'accion' },
  noTerminado: { titulo: 'No se pudo terminar', tono: 'mal' },
  llamadas: { titulo: 'Llamadas', tono: 'mal' },
  derivaciones: { titulo: 'Derivaciones', tono: 'bien' },
  sinDatos: { titulo: 'Sin datos', tono: 'neutro' },
  hechoSolo: { titulo: 'Cambios aplicados', tono: 'bien' },
};

/** «2 derivaciones creadas», «1 sin datos», «3 cambios aplicados»: la línea plegada de lo informativo. */
function fraseInfo(g: (typeof GRUPOS_INFO)[number], n: number): string {
  if (g === 'derivaciones') return n === 1 ? '1 derivación creada' : `${n} derivaciones creadas`;
  if (g === 'sinDatos') return `${n} sin datos`;
  return n === 1 ? '1 cambio aplicado' : `${n} cambios aplicados`;
}

const ETIQUETAS_ACCION = {
  aplicar: { enMarcha: 'Aplicando', hecho: 'Aplicado' },
  baja: { enMarcha: 'Dando de baja', hecho: 'De baja' },
} as const;

/** El botón de una propuesta o una baja según su estado. */
function BotonAccion({ tipo, boton, estado, enCurso, onPulsar }: { tipo: 'aplicar' | 'baja'; boton: string; estado: EstadoAccion; enCurso: boolean; onPulsar: () => void }) {
  const e = ETIQUETAS_ACCION[tipo];
  if (estado === 'pendiente' || estado === 'fallida') {
    return (
      <button type="button" className="action-btn" disabled={enCurso} aria-busy={enCurso || undefined} onClick={onPulsar}>
        {enCurso ? `${e.enMarcha}…` : boton}
      </button>
    );
  }
  if (estado === 'aplicando') return <span className="seg-estado">{e.enMarcha}</span>;
  if (estado === 'aplicada') return <span className="seg-estado seg-estado--bien">{e.hecho}</span>;
  if (estado === 'incierta') return <span className="seg-estado seg-estado--aviso">Sin confirmar</span>;
  return <span className="seg-estado">No aplicado</span>;
}

/**
 * Los avisos, solo si hay algo. A la vista, una caja con lo que pide un clic o falló (una línea por
 * cosa, como mucho 5 por grupo y «y N más»). Debajo, lo que es solo para informar, plegado en una
 * línea que se abre («2 derivaciones creadas · 1 sin datos»), sin botones.
 */
export function BloqueAvisos({ datos, estado, fallos, enCurso, m }: PropsVista) {
  const pendientes = GRUPOS_PENDIENTES.filter((g) => datos.avisos[g].length > 0);
  const info = GRUPOS_INFO.filter((g) => datos.avisos[g].length > 0);
  if (!pendientes.length && !info.length) return null;
  return (
    <section className="seg-avisos" aria-label="Avisos">
      {pendientes.length > 0 && (
        <div className="seg-pendientes">
          {pendientes.map((g) => {
            const todos = datos.avisos[g];
            const abierto = estado.grupos.includes(g);
            const vistos = abierto ? todos : todos.slice(0, AVISOS_A_LA_VISTA);
            const resto = todos.length - AVISOS_A_LA_VISTA;
            return (
              <div key={g} className={`seg-grupo seg-grupo--${GRUPO[g].tono}`}>
                <div className="seg-grupo-titulo">
                  <span className="seg-grupo-marca" aria-hidden="true" />
                  {GRUPO[g].titulo}
                  <span className="seg-grupo-num">{todos.length}</span>
                </div>
                <ul className="seg-grupo-lista">
                  {vistos.map((a) => <FilaAviso key={a.clave} a={a} fallo={falloDe(fallos, a.clave, a)} enCurso={enCurso.includes(a.clave)} m={m} />)}
                </ul>
                {resto > 0 && (
                  <button type="button" className="seg-mas" aria-expanded={abierto} onClick={() => m.verGrupo(g)}>
                    {abierto ? 'Ver menos' : `y ${resto} más`}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
      {info.length > 0 && (
        <details className="seg-info">
          <summary>
            <span className="seg-info-marca" aria-hidden="true" />
            {info.map((g) => fraseInfo(g, datos.avisos[g].length)).join(' · ')}
          </summary>
          {info.map((g) => (
            <div key={g} className={`seg-info-grupo seg-grupo--${GRUPO[g].tono}`} aria-label={GRUPO[g].titulo}>
              <div className="seg-info-titulo">{GRUPO[g].titulo}</div>
              <ul className="seg-grupo-lista">
                {datos.avisos[g].map((a) => <FilaAviso key={a.clave} a={a} fallo={falloDe(fallos, a.clave, a)} enCurso={enCurso.includes(a.clave)} m={m} />)}
              </ul>
            </div>
          ))}
        </details>
      )}
    </section>
  );
}

function FilaAviso({ a, fallo, enCurso, m }: { a: Aviso; fallo: string | null; enCurso: boolean; m: Manejadores }) {
  const acc = a.accion;
  const botones: ReactNode = acc ? <BotonAccion tipo={acc.tipo} boton={acc.boton} estado={acc.estado} enCurso={enCurso} onPulsar={() => m.accionAviso(a)} /> : null;
  const nota = acc && (acc.estado === 'fallida' || acc.estado === 'incierta') ? acc.nota : null;
  return (
    <li className="seg-aviso">
      <div className="seg-aviso-texto">
        {a.texto}
        {nota && <div className="seg-nota">{nota}</div>}
        {fallo && <div className="seg-error">{fallo}</div>}
      </div>
      {(botones || a.ficha) && (
        <div className="seg-aviso-acciones">
          {botones}
          {a.ficha && <a className="seg-enlace" href={a.ficha} target="_blank" rel="noopener noreferrer">Abrir en GHL</a>}
        </div>
      )}
    </li>
  );
}

/* ------------------------------------------------------------------------- *
 * Enviados
 * ------------------------------------------------------------------------- */


/** «Me contestó» con su segundo clic: saca a la persona de la secuencia. */
function MeContesto({ x, canal, estado, enCurso, m }: {
  x: { origen: ItemEnviado['origen']; id: string; secuenciaId: string | null }; canal: 'email' | 'whatsapp'; estado: EstadoPestana; enCurso: boolean; m: Manejadores;
}) {
  const clave = `contesto:${claveDe(x)}`;
  if (estado.contesto !== clave) {
    return <button type="button" className="seg-enlace" disabled={enCurso} onClick={() => m.pedirContesto(clave)}>Me contestó</button>;
  }
  return (
    <span className="seg-contesto">
      Sale de la secuencia.
      <button type="button" className="action-btn" disabled={enCurso} onClick={() => m.contestar(x, canal)}>Sí, me contestó</button>
      <button type="button" className="seg-enlace" onClick={() => m.pedirContesto(null)}>No</button>
    </span>
  );
}

const FILTROS: Array<{ f: EstadoPestana['filtro']; nombre: string }> = [
  { f: 'todos', nombre: 'Todos' }, { f: 'email', nombre: 'Correos' }, { f: 'whatsapp', nombre: 'WhatsApp' },
];
/** Cómo salió, después del canal: «Correo · Aprobado por ti», «WhatsApp · Visto en GHL». */
const NOMBRE_VIA: Record<ViaEnviado, string> = {
  auto: 'Automático', aprobado: 'Aprobado por ti', fuera: 'Desde hola@, fuera del panel', whatsapp: 'Marcado por ti', visto: 'Visto en GHL',
};
export const comoSalio = (x: Pick<ItemEnviado, 'canal' | 'via'>): string => `${x.canal === 'email' ? 'Correo' : 'WhatsApp'} · ${NOMBRE_VIA[x.via]}`;
const ESTADO_ENVIADO = {
  enviado: { texto: 'Enviado', tono: 'bien' },
  incierto: { texto: 'Sin confirmar', tono: 'aviso' },
  rebotado: { texto: 'Rebotó', tono: 'mal' },
} as const;

/** Los enviados por día de Madrid, del más nuevo al más viejo, con su encabezado («Hoy», «Ayer», «lun 5-oct»). */
export function porDia(xs: ItemEnviado[], ahora: Date): Array<{ dia: string; titulo: string; items: ItemEnviado[] }> {
  const grupos = new Map<string, ItemEnviado[]>();
  for (const x of [...xs].sort((a, b) => b.en.localeCompare(a.en))) {
    const d = diaDeMadrid(x.en);
    grupos.set(d, [...(grupos.get(d) ?? []), x]);
  }
  return [...grupos.entries()].map(([dia, items]) => ({ dia, titulo: fraseGrupoDia(dia, ahora), items }));
}

export function VistaEnviados({ datos, estado, fallos, enCurso, ahora, m }: PropsVista) {
  // «Fuera del panel» se suma al filtro del canal: los dos a la vez se pueden pulsar.
  const base = estado.soloFuera ? datos.enviados.filter((x) => x.origen === 'fuera') : datos.enviados;
  const lista = estado.filtro === 'todos' ? base : base.filter((x) => x.canal === estado.filtro);
  const fuera = datos.enviados.filter((x) => x.origen === 'fuera').length;
  return (
    <div className="seg-vista">
      <p className="seg-intro">
        Lo que salió en los últimos 7 días, del panel y de hola@ fuera de él (esto llega con unos 10 minutos de retraso). La app no confirma el envío ni la entrega de WhatsApp.
      </p>
      {datos.enviados.length > 0 && (
        <div className="seg-filtros" role="group" aria-label="Filtrar">
          {FILTROS.map(({ f, nombre }) => {
            const n = f === 'todos' ? base.length : base.filter((x) => x.canal === f).length;
            return (
              <button key={f} type="button" className={`seg-filtro${estado.filtro === f ? ' activo' : ''}`} aria-pressed={estado.filtro === f} onClick={() => m.filtrar(f)}>
                {f !== 'todos' && <IconoCanal canal={f} />}{nombre} {n}
              </button>
            );
          })}
          {/* Encendido, el botón se queda aunque ya no haya nada de fuera (una lectura fallida, o se salió de los 7 días): si no, nada lo apagaría. */}
          {(fuera > 0 || estado.soloFuera) && (
            <button type="button" className={`seg-filtro seg-filtro--fuera${estado.soloFuera ? ' activo' : ''}`} aria-pressed={estado.soloFuera} onClick={() => m.verFuera(!estado.soloFuera)}>
              Fuera del panel {fuera}
            </button>
          )}
        </div>
      )}
      {lista.length === 0 && <Vacia texto={datos.enviados.length === 0 ? 'Nada enviado en 7 días.' : 'Nada con este filtro.'} />}
      {porDia(lista, ahora).map((g) => (
        <section key={g.dia} className="seg-dia" aria-label={g.titulo}>
          <div className="seg-subtitulo">{g.titulo}</div>
          <ul className="seg-lista">
            {g.items.map((x) => {
              const clave = claveDe(x);
              return <FilaEnviado key={clave} x={x} estado={estado} fallo={falloDe(fallos, `contesto:${clave}`, x)} falloCorreo={falloDe(fallos, `correo:${clave}`, x)} leyendoCorreo={enCurso.includes(`correo:${clave}`)} enCurso={enCurso.includes(`contesto:${clave}`)} m={m} />;
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function FilaEnviado({ x, estado, fallo, falloCorreo, leyendoCorreo, enCurso, m }: { x: ItemEnviado; estado: EstadoPestana; fallo: string | null; falloCorreo: string | null; leyendoCorreo: boolean; enCurso: boolean; m: Manejadores }) {
  const clave = claveDe(x);
  const desplegado = estado.desplegado === clave;
  const correo = estado.correos[clave];
  const titulo = x.canal === 'email' ? correo?.asunto ?? x.asunto ?? 'Correo' : x.texto ?? 'WhatsApp';
  // Un WhatsApp que vio GHL salió de verdad (verde); uno que solo marcaste tú, lo dice tu marca (ámbar).
  const e = x.canal === 'whatsapp'
    ? (x.via === 'visto' ? { texto: 'Visto en GHL', tono: 'bien' } : { texto: 'Marcado como enviado por ti', tono: 'aviso' })
    : ESTADO_ENVIADO[x.estado];
  return (
    <li className="seg-fila seg-fila--enviado">
      <span className={`seg-semaforo seg-semaforo--${e.tono}`} role="img" aria-label={e.texto} title={e.texto} />
      <Avatar nombre={x.nombre} />
      <div className="seg-cuerpo">
        <Cabeza x={x} canal={x.canal} chip={x.chip} />
        <button type="button" className="seg-ver" aria-expanded={desplegado} onClick={() => m.desplegar(clave)}>
          <span className={x.canal === 'email' ? 'seg-asunto' : desplegado ? 'seg-burbuja' : 'seg-extracto'}>{titulo}</span>
        </button>
        {desplegado && x.canal === 'email' && (
          <div className="seg-correo">
            {leyendoCorreo && <p className="seg-meta" role="status">Cargando correo completo…</p>}
            {correo?.para && <div className="seg-para">Para: {correo.para}</div>}
            {(correo?.texto ?? x.texto) && <div className="seg-texto">{correo?.texto ?? x.texto}</div>}
            {correo?.estado === 'ok' && (
              <div className="seg-meta">
                {correo.adjuntosSinDato ? 'GHL no dice si llevaba adjuntos (salió desde Gmail).' : correo.adjuntos.length === 0 ? 'Sin adjuntos en el mensaje del proveedor.' : (
                  <div>Adjuntos<ul className="seg-fuentes">{correo.adjuntos.map((a, i) => (
                    <li key={`${a.nombre}:${i}`}>{a.url ? <a className="seg-enlace" href={a.url} target="_blank" rel="noopener noreferrer">{a.nombre}</a> : <span>{a.nombre} (sin enlace disponible)</span>}</li>
                  ))}</ul></div>
                )}
              </div>
            )}
            {correo?.estado === 'sin_mensaje' && <p className="seg-nota">No hay mensaje de proveedor disponible: los adjuntos no se han podido verificar.</p>}
            {correo?.estado === 'no_leido' && <p className="seg-nota">No se ha podido leer el correo completo: los adjuntos no se han podido verificar.</p>}
            {falloCorreo && <p className="seg-error">{falloCorreo}</p>}
            {(falloCorreo || (correo && correo.estado !== 'ok')) && <button type="button" className="seg-enlace" disabled={leyendoCorreo} onClick={() => m.desplegar(clave, true)}>Volver a cargar</button>}
          </div>
        )}
        <div className="seg-meta"><span>{juntar([comoSalio(x), fraseHora(x.en)])}</span><PuntosCiclo ciclo={x.ciclo} /></div>
        {x.nota && <div className="seg-nota">{x.nota}</div>}
        {fallo && <div className="seg-error">{fallo}</div>}
      </div>
      <div className="seg-accion">
        {/* Lo normal (enviado, o el WhatsApp ya dicho en su línea) lo dice el punto de color; la palabra, solo si hay algo que mirar. */}
        {x.estado !== 'enviado' && <span className={`seg-estado seg-estado--${e.tono}`}>{e.texto}</span>}
        {x.estado !== 'enviado' && x.ficha && <a className="seg-enlace" href={x.ficha} target="_blank" rel="noopener noreferrer">Abrir en GHL</a>}
        {x.meContesto && <MeContesto x={x} canal={x.canal} estado={estado} enCurso={enCurso} m={m} />}
      </div>
    </li>
  );
}

/* ------------------------------------------------------------------------- *
 * Secuencia (15-oct; la primera vista desde el 20-oct): las cifras, las personas por ciclo y las
 * personas por grupo. Solo mide: ni botones de llamada ni nada que hacer (las llamadas están en la
 * Manual Call de GHL). Cada fila, de un vistazo (20-oct, «full visual»): los ciclos en una pista de 8
 * tramos y los días en una barra contra los 21 que dura la secuencia, con la misma frase en palabras
 * para el lector de pantalla. Con un servidor que no manda «fueraDePlazo», ni la cifra ni el ámbar.
 * ------------------------------------------------------------------------- */

const dias = (n: number): string => `${n} ${n === 1 ? 'día' : 'días'}`;
const plural = (n: number, uno: string, varios: string): string => `${n} ${n === 1 ? uno : varios}`;

/** Una cifra: el nombre, el número grande y una línea de detalle. `aviso`: te necesita (en ámbar, la cifra entera). */
function Cifra({ nombre, valor, pie, aviso = false }: { nombre: string; valor: ReactNode; pie: ReactNode; aviso?: boolean }) {
  return (
    <div className={`seg-cifra${aviso ? ' seg-cifra--aviso' : ''}`}>
      <span className="seg-cifra-nombre">{nombre}</span>
      <span className={`seg-cifra-num${valor === 0 ? ' seg-cifra-num--cero' : ''}`}>{valor}</span>
      <span className={`seg-cifra-pie${aviso ? ' seg-cifra-pie--aviso' : ''}`}>{pie}</span>
    </div>
  );
}

/** Los toques de una persona (o de todas) por canal: teléfono, correo y WhatsApp, con su icono y la frase entera. */
function Toques({ t }: { t: ToquesHechos }) {
  const frase = `${plural(t.llamada, 'llamada', 'llamadas')}, ${plural(t.email, 'correo', 'correos')} y ${t.whatsapp} WhatsApp`;
  return (
    <span className="seg-toques" role="img" aria-label={`Toques: ${frase}`} title={frase}>
      {(['llamada', 'email', 'whatsapp'] as const).map((c, i) => (
        <Fragment key={c}>{i > 0 && ' '}<span className={`seg-toque seg-toque--${c}${t[c] === 0 ? ' seg-toque--cero' : ''}`}><IconoCanal canal={c} />{t[c]}</span></Fragment>
      ))}
    </span>
  );
}

export function CifrasSecuencia({ c }: { c: PanelSecuencia['cifras'] }) {
  const total = c.toques.llamada + c.toques.email + c.toques.whatsapp;
  const pct = c.entraron ? Math.round((c.respondieron.n / c.entraron) * 100) : null;
  const canales = (['llamada', 'email', 'whatsapp'] as const).filter((k) => c.respondieron.porCanal[k] > 0)
    .map((k) => `${c.respondieron.porCanal[k]} ${k === 'llamada' ? 'por teléfono' : k === 'email' ? 'por correo' : 'por WhatsApp'}`);
  const espera = c.esperan.n === 0 ? 'nadie espera'
    : juntar([`la más antigua, ${dias(c.esperan.masAntigua ?? 0)}`, c.esperan.conRetraso > 0 && `${c.esperan.conRetraso} con retraso`]);
  return (
    <div className="seg-cifras" role="group" aria-label="Resumen de la secuencia">
      <Cifra nombre="En secuencia" valor={c.enSecuencia} pie={`de ${c.entraron} que entraron`} />
      <Cifra nombre="Esperan tu llamada" valor={c.esperan.n} pie={espera} aviso={c.esperan.conRetraso > 0} />
      {c.fueraDePlazo !== null && (
        <Cifra nombre={`Pasan de ${DIAS_PLAZO_SECUENCIA} días`} valor={c.fueraDePlazo} pie={`de ${c.enSecuencia} dentro`} aviso={c.fueraDePlazo > 0} />
      )}
      <Cifra nombre="Toques hechos" valor={total} pie={<Toques t={c.toques} />} />
      <Cifra nombre="Respondieron" valor={pct === null ? '-' : `${pct} %`} pie={juntar([`${c.respondieron.n} de ${c.entraron}`, ...canales])} />
    </div>
  );
}

/** Alto de la columna más alta de «Por ciclo», en px. */
const ALTO_CICLO = 44;

/**
 * Las personas que siguen dentro, por ciclo: en anillo las que esperan tu llamada y rellenas las que ya
 * la tienen (lo mismo que dicen los tramos de la pista de cada fila).
 */
export function CiclosSecuencia({ porCiclo }: { porCiclo: PanelSecuencia['porCiclo'] }) {
  const max = Math.max(1, ...porCiclo.map((c) => c.esperan + c.enCurso));
  const alto = (n: number) => (n > 0 ? Math.max(3, Math.round((n / max) * (ALTO_CICLO - 2))) : 0);
  return (
    <figure className="seg-ciclos">
      <figcaption className="seg-subtitulo">Por ciclo, quienes siguen dentro</figcaption>
      <ol className="seg-ciclos-barras">
        {porCiclo.map((c) => {
          const n = c.esperan + c.enCurso;
          const frase = `Ciclo ${c.ciclo}: ${n === 0 ? 'nadie' : juntar([c.esperan > 0 && `${c.esperan} ${c.esperan === 1 ? 'espera' : 'esperan'} tu llamada`, c.enCurso > 0 && `${c.enCurso} con la llamada hecha`])}`;
          return (
            <li key={c.ciclo} className="seg-ciclos-col" title={frase} aria-label={frase}>
              <span className={`seg-ciclos-n${n === 0 ? ' seg-ciclos-n--cero' : ''}`} aria-hidden="true">{n}</span>
              <span className="seg-ciclos-barra" aria-hidden="true">
                {c.enCurso > 0 && <span className="seg-ciclos-tramo seg-ciclos-tramo--curso" style={{ height: alto(c.enCurso) }} />}
                {c.esperan > 0 && <span className="seg-ciclos-tramo seg-ciclos-tramo--espera" style={{ height: alto(c.esperan) }} />}
              </span>
              <span className="seg-ciclos-k" aria-hidden="true">{c.ciclo}</span>
            </li>
          );
        })}
      </ol>
      <div className="seg-leyenda" aria-hidden="true">
        <span><span className="seg-leyenda-marca seg-ciclos-tramo--espera" />Esperan tu llamada</span>
        <span><span className="seg-leyenda-marca seg-ciclos-tramo--curso" />Con la llamada hecha</span>
      </div>
    </figure>
  );
}

/** Dónde está la persona, en una línea: lo que espera, lo último que se hizo o por qué salió. */
export function situacionDe(f: FilaSecuencia, ahora: Date): string {
  if (f.espera) {
    const cuando = f.espera.dias === 0 ? 'Le toca tu llamada hoy' : `Espera tu llamada desde hace ${f.espera.dias} d`;
    return juntar([cuando, f.espera.retraso && 'con retraso']);
  }
  if (f.estado !== 'dentro') return f.salida ? juntar([f.salida.texto, f.salida.en && fraseDiaRelativo(f.salida.en, ahora)]) : 'Fuera de la secuencia';
  const falta = f.porEnviar.map((c) => (c === 'email' ? 'correo' : 'WhatsApp')).join(' y ');
  return juntar([f.ultimo ? `${f.ultimo.texto}, ${fraseDiaRelativo(f.ultimo.en, ahora)}` : 'Sin toques todavía', falta && `${falta} por enviar`]);
}

/** Lo que se dice cuando lleva más de DIAS_PLAZO_SECUENCIA días dentro (Joan, 20-oct). */
const TITULO_FUERA_DE_PLAZO = `Lo normal son ${DIAS_PLAZO_SECUENCIA} días: alguna llamada no se hizo cuando tocaba`;

/**
 * El tramo del ciclo en marcha (índice desde 0), o null: el que espera tu llamada (el siguiente a los
 * hechos) o, con la llamada ya hecha, el suyo. Quien salió no tiene ciclo en marcha.
 */
export function tramoActual(f: Pick<FilaSecuencia, 'estado' | 'ciclo' | 'hechos' | 'espera'>): number | null {
  if (f.estado !== 'dentro') return null;
  const hechos = Math.min(f.hechos, f.ciclo.de);
  if (f.espera) return hechos < f.ciclo.de ? hechos : null;
  return Math.min(Math.max(f.ciclo.n, 1), f.ciclo.de) - 1;
}

/** La pista en palabras: lo mismo que dibuja, para el lector de pantalla y al pasar el ratón. */
export function frasePista(f: Pick<FilaSecuencia, 'estado' | 'ciclo' | 'hechos' | 'espera'>): string {
  const hechos = Math.min(f.hechos, f.ciclo.de);
  const base = `${hechos} de ${f.ciclo.de} ciclos hechos`;
  const actual = tramoActual(f);
  if (actual === null) return base;
  if (f.espera) return `${base}; el ${actual + 1} espera tu llamada${f.espera.retraso ? ', con retraso' : ''}`;
  return `${base}; en el ${actual + 1}, con la llamada hecha`;
}

/**
 * Los ciclos como una pista de tramos (8): rellenos los hechos; el ciclo en marcha, recuadrado (hueco si
 * espera tu llamada, en ámbar si es con retraso). Al lado, «2 de 8».
 */
export function PistaCiclos({ f }: { f: Pick<FilaSecuencia, 'estado' | 'ciclo' | 'hechos' | 'espera'> }) {
  const hechos = Math.min(f.hechos, f.ciclo.de);
  const actual = tramoActual(f);
  const retraso = !!f.espera?.retraso;
  const frase = frasePista(f);
  return (
    <span className="seg-pista" role="img" aria-label={frase} title={frase}>
      <span className="seg-pista-tramos">
        {Array.from({ length: f.ciclo.de }, (_, i) => (
          <span key={i} className={`seg-pista-tramo${i < hechos ? ' seg-pista-tramo--hecho' : ''}${i === actual ? ` seg-pista-tramo--actual${retraso ? ' seg-pista-tramo--retraso' : ''}` : ''}`} />
        ))}
      </span>{' '}
      <span className="seg-pista-n">{hechos} de {f.ciclo.de}</span>
    </span>
  );
}

/** Hasta dónde llega la barra de días: el plazo (21) cae a tres cuartos y lo que pasa de él se ve. */
const ESCALA_DIAS = Math.round((DIAS_PLAZO_SECUENCIA * 4) / 3);

/** Los días dentro en palabras, contra el plazo. */
export function fraseDias(f: Pick<FilaSecuencia, 'diasSecuencia' | 'fueraDePlazo'>): string {
  return f.fueraDePlazo
    ? `${dias(f.diasSecuencia)} en la secuencia, más de ${DIAS_PLAZO_SECUENCIA}. ${TITULO_FUERA_DE_PLAZO}`
    : `${dias(f.diasSecuencia)} en la secuencia, de los ${DIAS_PLAZO_SECUENCIA} que dura`;
}

/** Los días dentro, en una barra con la marca de los 21; en ámbar cuando pasa de ellos. Al lado, «8 d». */
export function BarraDias({ f }: { f: Pick<FilaSecuencia, 'diasSecuencia' | 'fueraDePlazo'> }) {
  const ancho = Math.min(100, (100 * f.diasSecuencia) / ESCALA_DIAS);
  const frase = fraseDias(f);
  return (
    <span className={`seg-dias${f.fueraDePlazo ? ' seg-dias--fuera-de-plazo' : ''}`} role="img" aria-label={frase} title={frase}
      style={{ '--seg-dias-plazo': `${(100 * DIAS_PLAZO_SECUENCIA) / ESCALA_DIAS}%` } as CSSProperties}>
      <span className="seg-dias-barra">
        <span className="seg-dias-relleno" style={{ width: `${ancho}%` }} />
        <span className="seg-dias-marca" />
      </span>{' '}
      <span className="seg-dias-n">{f.diasSecuencia} d</span>
    </span>
  );
}

function FilaPersona({ f, ahora }: { f: FilaSecuencia; ahora: Date }) {
  const dentro = f.estado === 'dentro';
  const retraso = !!f.espera?.retraso;
  return (
    <li className={`seg-fila seg-fila--secuencia${dentro ? '' : ' seg-fila--fuera'}${retraso ? ' seg-fila--retraso' : ''}`}>
      <Avatar nombre={f.nombre} />
      <div className="seg-cuerpo">
        <Cabeza x={f} chip={null} extra={f.etapa ? <span className="seg-etapa" title="Etapa de su oportunidad en GHL">{f.etapa}</span> : null} />
        {/* Los espacios entre piezas no se ven (el hueco lo pone el CSS): son para el texto copiado o leído sin estilos. */}
        <div className="seg-visual">
          <PistaCiclos f={f} />{' '}
          <BarraDias f={f} />{' '}
          <Toques t={f.toques} />
          {f.diasDeal !== null && <>{' '}<span className="seg-deal" title="Días desde que se creó su oportunidad en GHL">deal {f.diasDeal} d</span></>}
        </div>
        <div className={`seg-situacion${retraso ? ' seg-situacion--retraso' : ''}`}>{situacionDe(f, ahora)}</div>
      </div>
      {f.ficha && (
        <div className="seg-accion">
          <a className="seg-enlace" href={f.ficha} target="_blank" rel="noopener noreferrer">Abrir en GHL</a>
        </div>
      )}
    </li>
  );
}

/** «Te toca llamar»: la que más espera, arriba; a igual espera, la de con retraso y luego la que más días lleva. */
export function ordenEspera(filas: FilaSecuencia[]): FilaSecuencia[] {
  return [...filas].sort((a, b) => (b.espera?.dias ?? -1) - (a.espera?.dias ?? -1)
    || Number(!!b.espera?.retraso) - Number(!!a.espera?.retraso) || b.diasSecuencia - a.diasSecuencia);
}

/** Un grupo de personas con su título y su número. */
function GrupoPersonas({ titulo, n, extra, children }: { titulo: string; n: number; extra?: ReactNode; children: ReactNode }) {
  return (
    <section className="seg-grupo-personas" aria-label={titulo}>
      <h3 className="seg-subtitulo">{titulo} · {n}{extra ? <><span className="seg-oculto">, </span>{extra}</> : null}</h3>
      {children}
    </section>
  );
}

export function VistaSecuencia({ lectura, ahora }: { lectura: LecturaSecuencia; ahora: Date }) {
  const p = lectura.datos;
  if (!p) {
    return (
      <div className="seg-vista">
        <Vacia texto={lectura.sinLeer ? 'No se ha podido leer la secuencia.' : 'Cargando…'} detalle={lectura.sinLeer ? 'Se vuelve a intentar sola.' : null} />
      </div>
    );
  }
  const dentro = p.filas.filter((f) => f.estado === 'dentro');
  const llamar = ordenEspera(dentro.filter((f) => f.espera));
  const enCurso = dentro.filter((f) => !f.espera);
  const fuera = p.filas.filter((f) => f.estado !== 'dentro');
  const conRetraso = llamar.filter((f) => f.espera?.retraso).length;
  const lista = (xs: FilaSecuencia[]) => <ul className="seg-lista">{xs.map((f) => <FilaPersona key={f.secuenciaId} f={f} ahora={ahora} />)}</ul>;
  return (
    <div className="seg-vista">
      <p className="seg-intro">
        Cada ciclo es tu llamada y, si no la coge, su correo y su WhatsApp: 8 ciclos, uno cada 2 días hábiles. Solo avanza cuando llamas.
        Cuenta lo de los últimos {p.ventanaDias} días.
      </p>
      {lectura.sinLeer && <p className="seg-nota">No se ha podido volver a leer: lo que ves puede no estar al día.</p>}
      <CifrasSecuencia c={p.cifras} />
      {dentro.length > 0 && <CiclosSecuencia porCiclo={p.porCiclo} />}
      {dentro.length === 0 && fuera.length === 0 && <Vacia texto="Nadie en la secuencia." detalle="Entra quien no coge una llamada tuya (o lleva la etiqueta «secuencia»)." />}
      {dentro.length > 0 && (
        <GrupoPersonas titulo="Te toca llamar" n={llamar.length} extra={conRetraso > 0 ? <span className="seg-subtitulo-aviso">{conRetraso} con retraso</span> : null}>
          {llamar.length > 0 ? lista(llamar) : <p className="seg-grupo-vacio">Nadie espera tu llamada.</p>}
        </GrupoPersonas>
      )}
      {enCurso.length > 0 && <GrupoPersonas titulo="En curso" n={enCurso.length}>{lista(enCurso)}</GrupoPersonas>}
      {fuera.length > 0 && (
        <details className="seg-fuera" open={dentro.length === 0}>
          <summary className="seg-subtitulo">Salieron en {p.ventanaDias} días · {fuera.length}</summary>
          {lista(fuera)}
        </details>
      )}
    </div>
  );
}
